import { type ReactNode, useSyncExternalStore } from "react";
import { useAuth } from "../auth/AuthContext";
import { extractPermissions } from "../project/jwtClaims";
import { authzStore } from "./authzStore";
import { DenyPage } from "./DenyPage";

const REQUIRED_PERMISSION = "Subject_Read";

interface AuthorizedGateProps {
    children: ReactNode;
}

/**
 * PRD-005 client-side advisory gate. Renders the deny page when:
 *   1. the locally decoded JWT lacks `Subject_Read` (client-side pre-flight,
 *      avoids letting the /auth/identity 403 be the first thing the user
 *      sees), OR
 *   2. a server response anywhere in the app returned 403 mid-session, which
 *      the API wrappers record in `authzStore` (the global error boundary
 *      requested by PRD-005 §SPA DoD).
 *
 * Branches:
 *   - jwt === null → render children. The PRD-002 paste-modal flow handles
 *     this state; the deny page is reserved for permission-absence only.
 *   - jwt has Subject_Read AND no mid-session 403 → render children.
 *   - jwt missing Subject_Read OR mid-session 403 → render the deny page.
 *
 * The server (every API GW Lambda + api-proxy) is the authoritative gate;
 * this component is a UX surface that routes both the pre-flight signal
 * and the post-call signal to the same well-known page.
 */
export function AuthorizedGate({ children }: AuthorizedGateProps) {
    const { jwt } = useAuth();
    const forcedDeny = useSyncExternalStore(
        authzStore.subscribe,
        authzStore.get,
        () => false,
    );
    if (jwt === null) {
        return <>{children}</>;
    }
    if (forcedDeny) {
        return <DenyPage />;
    }
    const permissions = extractPermissions(jwt);
    if (!permissions.includes(REQUIRED_PERMISSION)) {
        return <DenyPage />;
    }
    return <>{children}</>;
}
